/**
 * Utility functions for MenuScope text selection based on user permissions
 * Provides appropriate texts based on user access levels and states
 *
 * @module utils/menuMaker/menuScopeTexts
 */

/**
 * User state types for menu scope selection
 *
 * @enum {string}
 */
// eslint-disable-next-line no-shadow
export enum USER_SCOPE_STATE {
  /** New user who should start with organization */
  NEW_USER = 'newUser',
  /** User without organization access */
  WITHOUT_ORGANIZATION_ACCESS = 'withoutOrganizationAccess',
  /** User with full access to both store and organization */
  WITH_FULL_ACCESS = 'withFullAccess',
}

/**
 * Interface for menu scope text keys
 *
 * @interface MenuScopeTextKeys
 */
export interface MenuScopeTextKeys {
  /** Title text key */
  titleKey: string;
  /** Subtitle text key (optional) */
  subtitleKey?: string;
  /** Store description text key */
  storeDescriptionKey: string;
  /** Organization description text key */
  organizationDescriptionKey: string;
  /** No permission message key (optional) */
  noPermissionKey?: string;
  /** Recommended badge key */
  recommendedKey: string;
  /** Select button text key */
  selectKey: string;
  /** Organization label key */
  organizationKey: string;
}

/**
 * Gets menu scope text keys based on user state and permissions
 * Returns appropriate translation keys for the MenuScope component
 *
 * @param {boolean} isNewUser - Whether the user is new (when backend flag is available)
 * @param {boolean} hasOrganizationAccess - Whether user has organization level access
 * @returns {MenuScopeTextKeys} Object with all necessary translation keys
 *
 * @example
 * const textKeys = getMenuScopeTextKeys(false, true);
 * // Returns keys for user with full access
 */
export const getMenuScopeTextKeys = (isNewUser: boolean, hasOrganizationAccess: boolean): MenuScopeTextKeys => {
  // Determine user state
  let userState: USER_SCOPE_STATE;

  if (isNewUser) {
    userState = USER_SCOPE_STATE.NEW_USER;
  } else if (!hasOrganizationAccess) {
    userState = USER_SCOPE_STATE.WITHOUT_ORGANIZATION_ACCESS;
  } else {
    userState = USER_SCOPE_STATE.WITH_FULL_ACCESS;
  }

  // Common keys used across all states
  const commonKeys = {
    recommendedKey: 'menuMaker:menuScope.common.recommended',
    selectKey: 'menuMaker:menuScope.common.select',
    organizationKey: 'menuMaker:menuScope.common.organization',
  };

  // State-specific keys
  const stateKeys = {
    titleKey: `menuMaker:menuScope.${userState}.title`,
    storeDescriptionKey: `menuMaker:menuScope.${userState}.storeDescription`,
    organizationDescriptionKey: `menuMaker:menuScope.${userState}.organizationDescription`,
  };

  // Build result object
  const result: MenuScopeTextKeys = {
    ...commonKeys,
    ...stateKeys,
  };

  // Add optional keys based on state
  if (userState === USER_SCOPE_STATE.NEW_USER) {
    result.subtitleKey = `menuMaker:menuScope.${userState}.subtitle`;
  }

  if (userState === USER_SCOPE_STATE.WITHOUT_ORGANIZATION_ACCESS) {
    result.noPermissionKey = `menuMaker:menuScope.${userState}.noPermission`;
  }

  return result;
};

/**
 * Gets the sucursal label based on user state
 * Returns appropriate text for the store/branch card title
 *
 * @param {boolean} isNewUser - Whether the user is new
 * @param {string} storeName - Name of the store (e.g., "ParrotFood Centrito")
 * @returns {string} Appropriate label for sucursal card
 *
 * @example
 * const label = getSucursalLabel(false, "ParrotFood Centrito");
 * // Returns "ParrotFood Centrito" for existing users
 */
export const getSucursalLabel = (isNewUser: boolean, storeName?: string): string => {
  if (isNewUser) {
    return 'menuMaker:menuScope.newUser.store';
  }

  // For existing users, use store name or fallback to generic "Sucursal"
  return storeName || 'Sucursal';
};
